import { DownloadIcon, SyncIcon } from "@primer/octicons-react";
import { Box } from "@primer/react";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import SelectBoxThang from "../../components/selectbox-thang/SelectBoxThang";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { chieuCaoCanNangApi } from "../../api/chieuCaoCanNangApi";
import { crmHocSinhApi } from "../../api/crmHocSinhApi";
import { ComboboxTruong } from "../../components/combobox-truong";
import HocSinhShortInfo from "../../components/hoc-sinh/HocSinhShortInfo";
import SelectBoxTrangThaiHocSinhMultiple from "../../components/selectbox-trangthai-hocsinh-multiple";
import Button from "../../components/ui/button";
import DataGrid from "../../components-ui/data-grid";
import { useCommonContext } from "../../contexts/common";
import { useCommonSelectedHook } from "../../hooks/useCommonSelectedHook";
import { useMonths } from "../../hooks/useMonths";
import { ePageBaseStatus } from "../../models/ePageBaseStatus";
import { RootState } from "../../state/reducers";
import { getBMILabel, getGradeFromName } from "../sis/chieu-cao-can-nang/ChieuCaoCanNangPage";

const TongHopChieuCaoCanNang = () => {
  const { translate } = useCommonContext();
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { thangs } = useMonths();

  const [selectedTruongId, setSelectedTruongId] = useState<number>(0);
  const [dm_trangthaihocsinh_ids, setDmTrangThaiIds] = useState<number[]>([]);
  const [filterThang, setFilterThang] = useState<number>(new Date().getMonth() + 1);
  const [students, setStudents] = useState<any[]>([]);
  const [measurements, setMeasurements] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    if (!selectedTruongId || selectedTruongId === 0) {
      setStudents([]);
      setMeasurements([]);
      return;
    }

    setLoading(true);
    try {
      // 1. Fetch students for the school
      const resStudents = await crmHocSinhApi.select({
        nam_hoc: nam_hoc,
        searchText: "",
        dm_trangthaihocsinh_ids: [],
        dm_truong_id: selectedTruongId,
        dm_coso_id: dm_coso_id || 1,
        dm_khoi_id: 0,
        dm_he_id: 0,
        dm_lop_id: 0,
      });

      // 2. Fetch measurements for the school
      const resMeasurements = await chieuCaoCanNangApi.selectByTruong(nam_hoc, selectedTruongId);

      if (resStudents.is_success) {
        setStudents(resStudents.data || []);
      }
      if (resMeasurements.is_success) {
        setMeasurements(resMeasurements.data || []);
      }
    } catch (error) {
      console.error("Failed to load height/weight aggregation data", error);
    } finally {
      setLoading(false);
    }
  }, [nam_hoc, selectedTruongId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const dataSource = useMemo(() => {
    if (!students || students.length === 0) return [];

    // Filter measurements for the selected month
    const measurementsForMonth = measurements.filter((m: any) => m.thang === filterThang);

    return students
      .filter((hs: any) => {
        if (dm_trangthaihocsinh_ids && dm_trangthaihocsinh_ids.length > 0) {
          return dm_trangthaihocsinh_ids.includes(hs.dm_trangthaihocsinh_id);
        }
        return true;
      })
      .map((hs: any) => {
        const measurement = measurementsForMonth.find((m: any) => m.ts_hocsinh_id === hs.id);
        return {
          ...hs,
          ts_hocsinh_id: hs.id,
          chieu_cao: measurement?.chieu_cao,
          can_nang: measurement?.can_nang,
          bmi: measurement?.bmi,
          ngay_do: measurement?.ngay_do,
          id: measurement?.id ?? 0,
        };
      });
  }, [students, measurements, filterThang, dm_trangthaihocsinh_ids]);

  const handleExportExcel = () => {
    if (!dataSource || dataSource.length === 0) return;

    const exportData = dataSource.map((item: any, idx: number) => {
      const grade = getGradeFromName(item.ten_lop);
      const bmiLabel = getBMILabel(item.bmi, grade).label;
      return {
        "STT": idx + 1,
        "Mã học sinh": item.ma_hs ?? "",
        "Họ tên học sinh": item.ho_ten ?? "",
        "Trạng thái": item.trang_thai ?? "",
        "Trường": item.ten_truong ?? "",
        "Lớp": item.ten_lop ?? "",
        "Họ tên mẹ": item.ho_ten_lien_he_me ?? "",
        "Điện thoại mẹ": item.dien_thoai_lien_he_me ?? "",
        "Email mẹ": item.email_lien_he_me ?? "",
        "Họ tên bố": item.ho_ten_lien_he_cha ?? "",
        "Điện thoại bố": item.dien_thoai_lien_he_cha ?? "",
        "Email bố": item.email_lien_he_cha ?? "",
        "Chiều cao (cm)": item.chieu_cao ?? "",
        "Cân nặng (kg)": item.can_nang ?? "",
        "BMI": item.bmi != null ? Math.trunc(item.bmi * 10) / 10 : "",
        "Thể trạng": bmiLabel,
        "Ngày đo": item.ngay_do ? moment(item.ngay_do).format("DD/MM/YYYY HH:mm") : "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    ws["!cols"] = [
      { wch: 5 }, { wch: 15 }, { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 15 },
      { wch: 20 }, { wch: 15 }, { wch: 25 },
      { wch: 20 }, { wch: 15 }, { wch: 25 },
      { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 20 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tổng hợp CC-CN");
    XLSX.writeFile(wb, `Tong_hop_Chieu_cao_Can_nang_${moment().format("DD-MM-YYYY")}.xlsx`);
  };

  return (
    <Box sx={{ p: 3, overflowX: "auto" }}>
      <DataGrid
        height={window.innerHeight - 125}
        dataSource={dataSource}
        isLoading={loading}
        title={translate("Tổng hợp Chiều cao - Cân nặng")}
        subTitle={`${translate("Base.Label.TotalCount")}: ${dataSource.length}`}
        searchEnable
        paging={{
          enable: true,
          pageSizeItems: [20, 50, 100, 200, 500],
        }}
        actionComponent={
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            <ComboboxTruong
              value={selectedTruongId}
              onValueChanged={(value) => {
                setSelectedTruongId(value);
                setStudents([]);
                setMeasurements([]);
              }}
              isShowClearButton={true}
            />

            <SelectBoxTrangThaiHocSinhMultiple
              selectedValue={dm_trangthaihocsinh_ids}
              onSelectionChanged={(ids) => setDmTrangThaiIds(ids)}
              maxWidth={250}
              isShowClearBtn={true}
              hocSinhs={students}
            />

            <SelectBoxThang
              selectedValue={filterThang}
              onSelectionChanged={(id) => setFilterThang(id)}
              maxWidth={150}
            />

            <Button
              text="Base.Label.Refresh"
              leadingVisual={SyncIcon}
              size="medium"
              onClick={loadData}
            />

            <Button
              text="Export"
              leadingVisual={DownloadIcon}
              size="medium"
              variant="default"
              onClick={handleExportExcel}
            />
          </Box>
        }
        columns={[
          {
            caption: translate("Trạng thái"),
            dataField: "trang_thai",
            width: 120,
            align: "center",
            fixed: "left"
          },
          {
            caption: translate("Học sinh"),
            dataField: "ho_ten",
            width: 220,
            fixed: "left",
            cellRender: (data: any) => (
              <HocSinhShortInfo data={data} />
            )
          },
          {
            caption: translate("Lớp"),
            dataField: "ten_lop",
            width: 100,
            fixed: "left",
            cellRender: (data: any) => (
              <span>{data.ten_lop ?? "-"}</span>
            )
          },
          {
            caption: translate("Trường"),
            dataField: "ten_truong",
            width: 220,
            isVisible: false,
            cellRender: (data: any) => (
              <span>{data.ten_truong ?? "-"}</span>
            )
          },
          {
            caption: translate("Thông tin mẹ"),
            id: "thong_tin_me",
            width: 600,
            align: "center",
            columns: [
              {
                caption: translate("Họ tên"),
                dataField: "ho_ten_lien_he_me",
                width: 200
              },
              {
                caption: translate("Điện thoại"),
                dataField: "dien_thoai_lien_he_me",
                width: 120
              },
              {
                caption: translate("Email"),
                dataField: "email_lien_he_me",
                width: 250
              }
            ]
          },
          {
            caption: translate("Thông tin bố"),
            id: "thong_tin_bo",
            width: 600,
            align: "center",

            columns: [
              {
                caption: translate("Họ tên"),
                dataField: "ho_ten_lien_he_cha",
                width: 200
              },
              {
                caption: translate("Điện thoại"),
                dataField: "dien_thoai_lien_he_cha",
                width: 120
              },
              {
                caption: translate("Email"),
                dataField: "email_lien_he_cha",
                width: 250
              }
            ]
          },
          {
            caption: translate("Chiều cao - Cân nặng"),
            id: "chieu_cao_can_nang_group",
            width: 420,
            columns: [
              {
                caption: translate("Chiều cao (cm)"),
                dataField: "chieu_cao",
                width: 110,
                align: "center",
                cellRender: (data: any) => (
                  <span>{data.chieu_cao ?? "-"}</span>
                )
              },
              {
                caption: translate("Cân nặng (kg)"),
                dataField: "can_nang",
                width: 110,
                align: "center",
                cellRender: (data: any) => (
                  <span>{data.can_nang ?? "-"}</span>
                )
              },
              {
                caption: translate("BMI"),
                dataField: "bmi",
                width: 80,
                align: "center",
                cellRender: (data: any) => (
                  <span>{data.bmi != null ? Math.trunc(data.bmi * 10) / 10 : "-"}</span>
                )
              },
              {
                caption: translate("Thể trạng"),
                id: "the_trang",
                width: 120,
                align: "center",
                cellRender: (data: any) => {
                  const grade = getGradeFromName(data.ten_lop);
                  const { label, color } = getBMILabel(data.bmi, grade);
                  return (
                    <span style={{ color, fontWeight: 600 }}>{label}</span>
                  );
                }
              }
            ]
          }
        ]}
      />
    </Box>
  );
};

export default TongHopChieuCaoCanNang;
